import PasswordPage from "./views/auth/PasswordPage";
import { RouteErrorPage } from "./components/RouteErrorPage";
import CandidateWorklist from "./views/candidates/CandidateWorklist";
import { ReservationRouteAccess } from "./components/ReservationAccess";
import { ExamOperationsRoleAccess } from "./views/exams/ExamOperationsRoleAccess";
import { PRIORITIES_PATH } from "./views/exams/exam-operations.access";
import { lazy } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { ASSISTANT_LEGACY_PATH } from "./components/messaging/full-page";

/** Redirection d'une ancienne adresse en conservant ses paramètres (retours Stripe, liens enregistrés). */
function KeepQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={to + search + hash} replace />;
}

/** Ancienne page « Priorités de réservation » : onglet des Réglages, filtre (?preparation=) conservé. */
function PrioritiesRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const target = new URL(PRIORITIES_PATH, window.location.origin);
  params.forEach((value, key) => target.searchParams.set(key, value));
  return <Navigate to={target.pathname + target.search} replace />;
}
import { ProtectedLayout } from "./layouts/ProtectedLayout";
import { PublicLayout } from "./layouts/PublicLayout";
import { DevelopersLayout } from "./layouts/DevelopersLayout";

const GoogleCalendarCallbackPage = lazy(
  () => import("./views/oauth/GoogleCalendarCallbackPage"),
);
const TodayPage = lazy(() => import("./views/today/TodayPage"));
const AssistantRedirect = lazy(() => import("./views/ai/AssistantRedirect"));
const MessagesPage = lazy(() => import("./views/messages/MessagesPage"));
const StudentMessagesRedirect = lazy(
  () => import("./views/messages/StudentMessagesRedirect"),
);
const MyAccountPage = lazy(() => import("./views/compte/MyAccountPage"));
const ReglagesPage = lazy(() => import("./views/reglages/ReglagesPage"));
const FleetPage = lazy(() => import("./views/fleet/FleetPage"));
const NetworkDashboardPage = lazy(
  () => import("./views/network/NetworkDashboardPage"),
);
const WorkspaceReferencePage = lazy(
  () => import("./views/workspace/WorkspaceReferencePage"),
);
const WorkspaceNotesPage = lazy(
  () => import("./views/workspace/notes/NotesPage"),
);
const WorkspaceMeetingsPage = lazy(
  () => import("./views/workspace/meetings/MeetingsPage"),
);
const WorkspaceBoardsPage = lazy(
  () => import("./views/workspace/boards/BoardsPage"),
);
const WorkspaceTasksPage = lazy(
  () => import("./views/workspace/tasks/TasksPage"),
);
const PermioShopPage = lazy(() => import("./views/permio-shop/PermioShopPage"));
const EmailSequencesPage = lazy(
  () => import("./views/workspace/sequences/EmailSequencesPage"),
);
const CguPage = lazy(() => import("./views/legal/CguPage"));
const DpaPage = lazy(() => import("./views/legal/DpaPage"));
const PrivacyPage = lazy(() => import("./views/legal/PrivacyPage"));
const UnsubscribePage = lazy(() => import("./views/auth/UnsubscribePage"));
const LandingPage = lazy(() => import("./views/auth/LandingPage"));
const AccountAccessPage = lazy(() => import("./views/auth/AccountAccessPage"));
const CandidatesPage = lazy(() => import("./views/candidates/CandidatesPage"));
const ImportPage = lazy(() => import("./views/reglages/import/ImportPage"));
const BookingsPage = lazy(() => import("./views/bookings/BookingsPage"));
const ExamsPage = lazy(() => import("./views/exams/ExamsPage"));
const PrevisionsPage = lazy(() => import("./views/exams/previsions/PrevisionsPage"));
const SlotsPage = lazy(() => import("./views/slots/SlotsPage"));
const HelpPage = lazy(() => import("./views/help/HelpPage"));
const GuidePage = lazy(() => import("./views/guide/GuidePage"));
const RdvOauthCallbackPage = lazy(
  () => import("./views/oauth/RdvOauthCallbackPage"),
);
const SeuilPage = lazy(() => import("./views/seuil/SeuilPage"));
const EtpsRedirect = lazy(() => import("./views/seuil/SeuilPage").then((m) => ({ default: m.EtpsRedirect })));
const RequestsPage = lazy(() => import("./views/requests/RequestsPage"));
const NewRequestPage = lazy(() => import("./views/requests/NewRequestPage"));
const BillingSuccessPage = lazy(
  () => import("./views/subscription/BillingSuccessPage"),
);
const BillingCancelPage = lazy(
  () => import("./views/subscription/BillingCancelPage"),
);
const CheckoutPage = lazy(() => import("./views/subscription/CheckoutPage"));
const PlanningPage = lazy(() => import("./views/planning/PlanningPage"));
const LessonsPlanningPage = lazy(
  () => import("./views/lessons/LessonsPlanningPage"),
);
const InstructorsPage = lazy(
  () => import("./views/instructors/InstructorsPage"),
);
const CatalogPage = lazy(() => import("./views/catalog/CatalogPage"));
const BillingPage = lazy(() => import("./views/billing/BillingPage"));
const QuoteFormPage = lazy(() => import("./views/quotes/QuoteFormPage"));
const InvoiceFormPage = lazy(() => import("./views/invoices/InvoiceFormPage"));
const PaymentsPage = lazy(() => import("./views/payments/PaymentsPage"));
const ApiDocsPage = lazy(() => import("./views/developers/ApiDocsPage"));
const SmartQueuePage = lazy(() => import("./views/candidates/SmartQueuePage"));
const CandidateDetailPage = lazy(
  () => import("./views/candidates/CandidateDetailPage"),
);
const ContractTemplatesPage = lazy(
  () => import("./views/contracts/ContractTemplatesPage"),
);
const ContractsPage = lazy(() => import("./views/contracts/ContractsPage"));
const ContractSignaturePage = lazy(
  () => import("./views/contracts/ContractSignaturePage"),
);
const ContractFormPage = lazy(
  () => import("./views/contracts/ContractFormPage"),
);
const LivretPage = lazy(() => import("./views/livret/LivretPage"));
// Nuancier de la charte : développement local uniquement. En production,
// `import.meta.env.DEV` vaut false : la route et son code sont retirés du build.
const NuancierPage = import.meta.env.DEV
  ? lazy(() => import("./views/dev/NuancierPage"))
  : null;

export const router = createBrowserRouter([
  { errorElement: <RouteErrorPage />, children: [
  // ── Public ──
  {
    element: <PublicLayout />,
    children: [
      { path: "/", element: <LandingPage /> },
      { path: "/forgot-password", element: <PasswordPage mode="forgot" /> },
      { path: "/reset-password", element: <PasswordPage mode="reset" /> },
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/cgu", element: <CguPage /> },
      { path: "/dpa", element: <DpaPage /> },
      { path: "/desinscription", element: <UnsubscribePage /> },
      {
        path: "/register",
        element: <AccountAccessPage key="register" register />,
      },
      {
        path: "/login",
        element: <AccountAccessPage key="login" />,
      },
      {
        path: "/rdv-oauth",
        element: <RdvOauthCallbackPage />,
      },
      {
        path: "/billing/success",
        element: <BillingSuccessPage />,
      },
      {
        path: "/billing/cancel",
        element: <BillingCancelPage />,
      },
    ],
  },
  // ── Developers portal (public, no auth) ──
  {
    path: "/developers",
    element: <DevelopersLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/developers/api" replace />,
      },
      {
        path: "api",
        element: <ApiDocsPage />,
      },
    ],
  },
  // ── App (authenticated) ──
  {
    element: <ProtectedLayout />,
    children: [
      { path: "/today", element: <TodayPage /> },
      // Ancienne page « Permio AI » : mène à sa conversation dans la messagerie (`/messages?type=assistant`).
      { path: ASSISTANT_LEGACY_PATH, element: <AssistantRedirect /> },
      { path: "/messages", element: <MessagesPage /> },
      // Ancienne page « Messages des élèves » : mène à la messagerie en pleine page (`/messages`).
      { path: "/messages/eleves", element: <StudentMessagesRedirect /> },
      { path: "/fleet", element: <FleetPage /> },
      { path: "/fleet/availability", element: <FleetPage key="journee" initialTab="journee" /> },
      {
        path: "/fleet/candidate-availability",
        element: <FleetPage key="journee-eleves" initialTab="journee" initialMode="eleves" />,
      },
      { path: "/network", element: <NetworkDashboardPage /> },
      {
        path: "/statistics",
        element: <InstructorsPage key="statistics" initialTab="statistiques" />,
      },
      // Vue d'ensemble retirée le 12/10/2026 (réorganisation « Examens ») : l'adresse mène à Réservation d’examen,
      // paramètres conservés (retours de connexion RdvPermis et de paiement).
      {
        path: "/dashboard",
        element: <KeepQuery to="/requests" />,
      },

      {
        path: "/candidates",
        element: <CandidatesPage />,
      },
      {
        path: "/candidates/administrative",
        element: <CandidateWorklist />,
      },
      {
        path: "/candidates/smart-queue",
        element: <SmartQueuePage />,
      },
      {
        path: "/candidates/:id",
        element: <CandidateDetailPage />,
      },
      {
        path: "/planning",
        element: <PlanningPage />,
      },
      {
        path: "/workspace/reference",
        element: <WorkspaceReferencePage />,
      },
      {
        path: "/workspace/notes",
        element: <WorkspaceNotesPage />,
      },
      {
        path: "/settings/google/callback",
        element: <GoogleCalendarCallbackPage />,
      },
      {
        path: "/workspace/meetings",
        element: <WorkspaceMeetingsPage />,
      },
      {
        path: "/workspace/boards",
        element: <WorkspaceBoardsPage />,
      },
      {
        path: "/workspace/tasks",
        element: <WorkspaceTasksPage />,
      },
      {
        path: "/workspace/sequences",
        element: <EmailSequencesPage />,
      },
      {
        path: "/boutique-permio",
        element: <PermioShopPage />,
      },
      {
        path: "/lessons",
        element: <LessonsPlanningPage />,
      },
      {
        path: "/bookings",
        element: <BookingsPage />,
      },
      {
        path: "/slots",
        element: <SlotsPage />,
      },
      {
        // Déplacée dans Réglages › RdvPermis et réservation (19/10/2026) ; un rôle non admis garde son message ici.
        path: "/exams/preparation",
        element: (
          <ExamOperationsRoleAccess>
            <PrioritiesRedirect />
          </ExamOperationsRoleAccess>
        ),
      },
      {
        path: "/exams",
        element: <ExamsPage />,
      },
      {
        // Prévisions (19/10/2026) : prévisions des enseignants et demandes des élèves, deux listes d'une même page.
        path: "/exams/forecasts",
        element: <PrevisionsPage />,
      },
      {
        path: "/exams/student-requests",
        element: <Navigate to="/exams/forecasts?liste=eleves" replace />,
      },
      {
        path: "/notifications",
        element: <KeepQuery to="/reglages/notifications" />,
      },
      {
        path: "/password",
        element: <KeepQuery to="/compte" />,
      },
      {
        path: "/security",
        element: <KeepQuery to="/reglages/donnees" />,
      },
      {
        path: "/settings",
        element: <KeepQuery to="/reglages/rdvpermis" />,
      },
      {
        path: "/settings/ai",
        element: <KeepQuery to="/reglages/ia" />,
      },
      {
        path: "/profile",
        element: <KeepQuery to="/reglages/ecole" />,
      },
      {
        path: "/account-settings",
        element: <KeepQuery to="/reglages/equipe" />,
      },
      // Réglages refaits (09/10/2026) : compte personnel et réglages de l'auto-école ; anciennes adresses redirigées.
      { path: "/compte", element: <MyAccountPage /> },
      { path: "/reglages", element: <ReglagesPage /> },
      { path: "/reglages/:section", element: <ReglagesPage /> },
      { path: "/reglages/import/:kind", element: <ImportPage /> },
      {
        path: "/help",
        element: <HelpPage />,
      },
      // Guide d'utilisation (24/10/2026) : écrans et procédures métier, les textes que lit Permio AI.
      { path: "/guide", element: <GuidePage /> },
      { path: "/guide/:page", element: <GuidePage /> },
      { path: "/guide/procedures/:id", element: <GuidePage /> },
      {
        path: "/subscriptions",
        element: <KeepQuery to="/reglages/abonnement" />,
      },
      {
        path: "/checkout",
        element: <CheckoutPage />,
      },
      {
        path: "/instructors",
        element: <InstructorsPage />,
      },
      {
        path: "/catalog",
        element: <CatalogPage />,
      },
      {
        path: "/orders",
        element: <BillingPage key="orders" section="orders" />,
      },
      // Import de données : déplacé dans Réglages › Import (19/10/2026).
      { path: "/candidates/import", element: <KeepQuery to="/reglages/import/eleves" /> },
      { path: "/candidates/import/planning", element: <KeepQuery to="/reglages/import/lecons" /> },
      { path: "/candidates/import/restitution", element: <KeepQuery to="/reglages/import/restitution" /> },
      {
        path: "/orders/new",
        element: <QuoteFormPage key="new-order" orderMode />,
      },
      {
        path: "/orders/:id/edit",
        element: <QuoteFormPage key="edit-order" orderMode />,
      },
      {
        path: "/quotes",
        element: <BillingPage key="quotes" section="quotes" />,
      },
      {
        path: "/quotes/new",
        element: <QuoteFormPage />,
      },
      {
        path: "/quotes/:id/edit",
        element: <QuoteFormPage />,
      },
      {
        path: "/invoices",
        element: <BillingPage key="invoices" section="invoices" />,
      },
      {
        // Facture directe retirée : une facture se prépare depuis sa commande.
        path: "/invoices/new",
        element: <Navigate to="/orders" replace />,
      },
      {
        path: "/invoices/:id/edit",
        element: <InvoiceFormPage />,
      },
      {
        path: "/payments",
        element: <PaymentsPage />,
      },
      {
        path: "/contracts",
        element: <ContractsPage />,
      },
      {
        path: "/contracts/new",
        element: <ContractFormPage />,
      },
      {
        path: "/contracts/:id/signature",
        element: <ContractSignaturePage />,
      },
      {
        path: "/contracts/:id/edit",
        element: <ContractFormPage />,
      },
      {
        path: "/contracts/templates",
        element: <ContractTemplatesPage />,
      },
      {
        // Seuil formateur et ETP réunis (12/10/2026) : /etps ouvre l'onglet ETP.
        path: "/etps",
        element: <EtpsRedirect />,
      },
      {
        path: "/windows",
        element: (
          <ReservationRouteAccess>
            <SeuilPage />
          </ReservationRouteAccess>
        ),
      },
      {
        path: "/requests",
        element: (
          <ReservationRouteAccess>
            <RequestsPage />
          </ReservationRouteAccess>
        ),
      },
      {
        path: "/requests/new",
        element: (
          <ReservationRouteAccess>
            <NewRequestPage />
          </ReservationRouteAccess>
        ),
      },
      {
        path: "/requests/:id/edit",
        element: (
          <ReservationRouteAccess>
            <NewRequestPage />
          </ReservationRouteAccess>
        ),
      },
      {
        path: "/diagnostic",
        element: <KeepQuery to="/reglages/diagnostic" />,
      },
      {
        path: "/livret",
        element: <LivretPage />,
      },
    ],
  },
  // ── Développement local (absent du build de production) ──
  ...(NuancierPage
    ? [{ path: "/dev/nuancier", element: <NuancierPage /> }]
    : []),
  {
    path: "*",
    element: <Navigate to="/today" replace />,
  },
  ] },
]);
